/**
 * Hub portfolio submit — phase machine for card collapse / pulse.
 *
 * Belongs here: pending → submitting → submitted UI phases (pending visit only).
 * Does not belong here: POST / retries / toast (see useSubmitPortfolioMutation);
 *   cold restore (see HubSubmittedScreen via ExpertReviewHubPage).
 */
import { useEffect, useRef, useState } from 'react'
import { useSubmitPortfolioMutation } from '../api/useSubmitPortfolioMutation'

/** pending → submitting → submitted. Driven by CTA on the pending visit only. */
export enum HubSubmitPhase {
  Pending = 'pending',
  Submitting = 'submitting',
  Submitted = 'submitted'
}

// Wait for HUB_SPRING (~500ms) plus a beat before pulse — otherwise pulse fights the move.
const HUB_SETTLE_MS = 650

/**
 * Owns submit phase + when the card may start its CSS pulse.
 * Mount only under the interactive hub visit.
 *
 * @returns phase for UI; shouldPlayCardSubmitPulse only after the card has finished centering;
 *   firstPlanDropTime from POST /submit only (null until then).
 *   Do not read status here — that epoch is for the cold submitted visit.
 */
export function useHubSubmit() {
  const { submitPortfolio } = useSubmitPortfolioMutation()
  const [phase, setPhase] = useState<HubSubmitPhase>(HubSubmitPhase.Pending)
  const [firstPlanDropTime, setFirstPlanDropTime] = useState<number | null>(null)
  // True once centering spring has settled — gates hub-card--pulse CSS
  const [hasCardFinishedCentering, setHasCardFinishedCentering] = useState(false)
  const settleTimeoutRef = useRef<number | null>(null)

  // Clear pending settle timer if the hub unmounts mid-submit
  useEffect(() => {
    return () => {
      if (settleTimeoutRef.current !== null) window.clearTimeout(settleTimeoutRef.current)
    }
  }, [])

  // Pulse only while submitting AND after Framer layout settle — not during the move
  const shouldPlayCardSubmitPulse = phase === HubSubmitPhase.Submitting && hasCardFinishedCentering

  useEffect(() => {
    // Left submitting (or never entered) — reset settle so the next submit waits again
    if (phase !== HubSubmitPhase.Submitting) {
      setHasCardFinishedCentering(false)
      return
    }
    // Defer pulse until card has finished sliding to center
    settleTimeoutRef.current = window.setTimeout(() => {
      settleTimeoutRef.current = null
      setHasCardFinishedCentering(true)
    }, HUB_SETTLE_MS)
    return () => {
      if (settleTimeoutRef.current !== null) window.clearTimeout(settleTimeoutRef.current)
    }
  }, [phase])

  /** Begin submit when all steps are done and we are still idle. */
  const startSubmit = (canSubmit: boolean) => {
    if (!canSubmit || phase !== HubSubmitPhase.Pending) return
    setPhase(HubSubmitPhase.Submitting)
    void (async () => {
      try {
        const submitResponse = await submitPortfolio()
        setFirstPlanDropTime(submitResponse?.firstPlanDropTime ?? null)
        setPhase(HubSubmitPhase.Submitted)
      } catch {
        // Toast already shown in useSubmitPortfolioMutation — restore interactive hub
        setFirstPlanDropTime(null)
        setPhase(HubSubmitPhase.Pending)
      }
    })()
  }

  return { phase, shouldPlayCardSubmitPulse, startSubmit, firstPlanDropTime }
}
